import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import {
  ClientWithAssignments,
  Country,
  GridDrawerComponent,
} from '../../models';
import { CountryMaterial } from '../../models/country-material';
import { ClientService } from '../../services';

@Component({
  standalone: false,
  selector: 'gstar-assign-country-material',
  templateUrl: 'assign-country-material.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignCountryMaterialComponent
  extends GridDrawerComponent<ClientWithAssignments>
  implements OnInit
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly clientService = inject(ClientService);

  public readonly maxNameLength = 255;
  enableUpdate: boolean = false;

  public form: FormGroup = this.formBuilder.group({
    countryAssignments: this.formBuilder.array([]),
  });

  get countryAssignments(): FormArray<FormControl<CountryMaterial | null>> {
    return this.form.get('countryAssignments') as FormArray;
  }

  prevModel: WritableSignal<ClientWithAssignments> = signal(
    {} as ClientWithAssignments
  );

  destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values: ClientWithAssignments) => {
        if (this.isEdit) this.checkChanges(values);
        this.modelChange.emit({
          model: {
            ...this.model,
            ...values,
          },
          prevModel: this.prevModel(),
          isValid:
            this.isEdit && this.form.valid
              ? this.enableUpdate
              : this.form.valid,
        });
      });
    if (!this.model) return;

    this.clientService
      .getCountryAssignment(this.model.clientId)
      .subscribe(countryAssignments => {
        this.initForm(countryAssignments);
        this.sortCountryAssignments();
      });
  }

  initForm(countryAssignments: CountryMaterial[]): void {
    this.form.patchValue({
      name: this.model?.name,
      isActive: !!this.model?.isActive,
    });
    countryAssignments.forEach(countryAssignment => {
      const newFormControl = this.formBuilder.control(countryAssignment);
      this.countryAssignments.push(newFormControl);
    });

    this.prevModel.set(
      cloneDeep({
        ...this.model!,
        countryAssignments,
      })
    );
  }

  countrySelect(country: Country): void {
    if (this.isCountryAlreadyAdded(country) || !country.countryId) return;
    const newFormControl = this.formBuilder.control(country);
    this.countryAssignments.push(newFormControl);
    this.sortCountryAssignments();
  }

  removeAssignment(index: number): void {
    this.countryAssignments.removeAt(index);
  }

  isCountryAlreadyAdded(country: Country): boolean {
    return this.countryAssignments.value.some(
      (addedCountry: Country | null) =>
        addedCountry?.countryId === country.countryId
    );
  }

  private sortCountryAssignments(): void {
    this.countryAssignments.controls.sort(
      (a: FormControl<Country | null>, b: FormControl<Country | null>) => {
        const nameA = (a.value?.name || '').toUpperCase();
        const nameB = (b.value?.name || '').toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      }
    );
  }

  private checkChanges(values: ClientWithAssignments): void {
    let hasCountryChanged: boolean = false;

    if (values && this.prevModel().countryAssignments) {
      if (
        values.countryAssignments.length !==
        this.prevModel().countryAssignments?.length
      ) {
        this.enableUpdate = true;
      } else {
        values.countryAssignments.forEach((country: CountryMaterial) => {
          if (
            !this.prevModel().countryAssignments.some(
              (c: CountryMaterial) => c.countryId === country.countryId
            )
          ) {
            hasCountryChanged = true;
          }
        });
        this.enableUpdate = hasCountryChanged;
      }
    }
  }
}
